<script lang="ts">
	import { page } from '$app/state';
	import { locales, localizeHref } from '$lib/paraglide/runtime';

	import Analytics from '$lib/components/Analytics.svelte';
	import ChatWidget from '$lib/components/chat/ChatWidget.svelte';
	import Footer from '$lib/components/Footer.svelte';
	import Header from '$lib/components/Header/Header.svelte';
	import { setCategoriesContext, setProductsContext } from '$lib/context';
	import { organization, website } from '$lib/seo/schema';
	import '../app.css';
	import type { LayoutProps } from './$types';

	let { children, data }: LayoutProps = $props();

	// Getters, not the arrays themselves: `setContext` only runs at init, so a
	// snapshot would go stale if `data` changes. See `$lib/context`.
	setProductsContext(() => data.products);
	setCategoriesContext(() => data.categories);
</script>

<svelte:head>
	<link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
	<link rel="icon" type="image/svg+xml" href="/favicon.svg" />
	<link rel="shortcut icon" href="/favicon.ico" />
	<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
	<link rel="manifest" href="/site.webmanifest" />

	<!--
		Publisher identity, emitted once per page from the layout rather than
		repeated by every route's `Seo`. Page-level graphs reference these by
		`@id`, so a product or an article names its publisher without restating it.
	-->
	{@html `<script type="application/ld+json">${JSON.stringify({
		'@context': 'https://schema.org',
		'@graph': [organization(), website(data.locale)]
	})}<\/script>`}
</svelte:head>

<!--
	Paraglide's SSG locale-link helper is deliberately not used here. It emits
	`localizeHref(currentPath, locale)` for every locale, which assumes a
	localised URL differs from the default only by its prefix. WPML translates
	the category segment too, so that produced invalid URLs like
	`/shop/เมล็ดกัญชา/…` (English prefix, Thai category). Localised paths are
	enumerated explicitly instead — see scripts/prerender-entries.mjs.
-->

<Analytics />

<!--
	White ground, matching dutch-passion.com. Sections opt into `bg-surface-muted`
	for band rhythm rather than the page painting grey and sections opting out.

	`min-h-screen flex flex-col` + `flex-1` pins the footer: without it a short
	page (or iOS overscroll) shows the body colour below a now-dark footer.
-->
<div class="bg-surface flex min-h-screen flex-col">
	<Header />
	<div class="flex-1">{@render children()}</div>
	<Footer />
</div>

<!--
	Outside the layout column, because it is fixed-position and belongs to the
	viewport rather than to the document flow. It renders nothing until it mounts
	and nothing at all outside `en` and `th` — see the component.
-->
<ChatWidget locale={data.locale} />
